import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { useState, useCallback, useMemo } from "react";

type Props = {
  visible: boolean;
  onHide: () => void;
  onSubmit: (reason: string) => void;
  defectCode: string;
};

export const GenericCodeReasonDialog = (props: Props) => {
  const { visible, onHide, onSubmit, defectCode } = props;
  
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleSubmit = useCallback(() => {
    if (reason.trim().length < 3) {
      setError("Reason must be at least 3 characters");
      return;
    }
    if (reason.trim().length > 255) {
      setError("Reason must not exceed 255 characters");
      return;
    }
    
    onSubmit(reason.trim());
    setReason("");
    setError("");
  }, [reason, onSubmit]);

  const handleCancel = useCallback(() => {
    setReason("");
    setError("");
    onHide();
  }, [onHide]);

  const footerContent = useMemo(() => (
    <div className="flex justify-content-end gap-2">
      <Button 
        label="Cancel" 
        onClick={handleCancel}
        className="p-button-secondary"
      />
      <Button 
        label="Submit" 
        onClick={handleSubmit}
        disabled={reason.trim().length < 3 || reason.trim().length > 255}
      />
    </div>
  ), [reason, handleSubmit, handleCancel]);

  return (
    <Dialog
      header="Reason for general code"
      visible={visible}
      onHide={handleCancel}
      footer={footerContent}
      draggable={false}
      className="w-6"
    >
      <div className="flex flex-column gap-3">
        <p>
          You have selected a general defect code: <strong>{defectCode}</strong>
        </p>
        <p>
          Choosing to assign a general code may mean the specific code you need is not available. Please submit a short description of the option that is missing (3-255 characters).
        </p>
        
        <InputTextarea
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError("");
          }}
          rows={4}
          placeholder="Add short description..."
          className={error ? "p-invalid" : ""}
        />
        
        {error && (
          <small className="p-error">{error}</small>
        )}
        
        <small className={reason.length > 255 ? "p-error" : "text-600"}>
          Characters: {reason.length}/255
        </small>
      </div>
    </Dialog>
  );
};
